import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import random from 'lodash/random';

import gettext from 'js-utils/gettext';

import Button from 'eventbrite_design_system/button/Button';
import IconButton from 'eventbrite_design_system/iconButton/IconButton';
import Illustration from 'eventbrite_design_system/illustration/Illustration';
import CrossChunkySvg from 'eventbrite_design_system/iconography/icons/CrossChunky';
import withOverlayControls from 'eventbrite_design_system/structure/hoc/withOverlayControls';

import SuperCropOneSvg from '../resources/superCropOne';
import SuperCropTwoSvg from '../resources/superCropTwo';
import SuperCropThreeSvg from '../resources/superCropThree';

import { shouldShowGDPRModal } from '../utils';
import { GDPRModalContent } from '../components';

import {
    FOLLOW_CTA,
    GA_SHOW_GDPR_MODAL,
    ORGANIZER_IMAGE_DIAMETER,
} from '../constants';
import './FollowCard.scss';

const SUPER_CROPS = [
    <SuperCropOneSvg key="supercrop-one" />,
    <SuperCropTwoSvg key="supercrop-two" />,
    <SuperCropThreeSvg key="supercrop-three" />,
];

const OrganizerProfileImage = ({ imageUrl, organizerName, isSquare }) => {
    const randomSuperCropImage = SUPER_CROPS[random(0, 2)];

    const imageContainerClasses = classnames('follow-card__image-container', {
        'follow-card__image-container--square': isSquare,
    });

    let organizerImage = null;

    if (imageUrl) {
        organizerImage = (
            <img
                className="follow-card__image"
                src={imageUrl}
                width={ORGANIZER_IMAGE_DIAMETER}
                height={ORGANIZER_IMAGE_DIAMETER}
                alt={gettext(`${organizerName} profile image.`)}
            />
        );
    } else {
        organizerImage = (
            <div className="follow-card__image-placeholder">
                <Illustration
                    type={randomSuperCropImage}
                    width={ORGANIZER_IMAGE_DIAMETER}
                    height={ORGANIZER_IMAGE_DIAMETER}
                />
            </div>
        );
    }

    return <div className={imageContainerClasses}>{organizerImage}</div>;
};

class FollowCard extends React.Component {
    static propTypes = {
        id: PropTypes.string.isRequired,
        imageUrl: PropTypes.string,
        isFollowing: PropTypes.bool,
        url: PropTypes.string,
        onClose: PropTypes.func.isRequired,
        onFollow: PropTypes.func.isRequired,
        onLinkClick: PropTypes.func,
        handleSetError: PropTypes.func,
        trackGAEvent: PropTypes.func,
        title: PropTypes.string.isRequired,
        subtitle: PropTypes.node,
        dataPointOne: PropTypes.node,
        type: PropTypes.string,
        userId: PropTypes.string,
        isGDPRCountry: PropTypes.bool.isRequired,
        isSquareImage: PropTypes.bool,
        gdprType: PropTypes.oneOf(['organizer', 'collection']),
        launchCommSettingsFollowAction: PropTypes.bool,
        showOverlay: PropTypes.func,
        hideOverlay: PropTypes.func,
    };

    _informUser = async () => {
        const {
            showOverlay,
            hideOverlay,
            trackGAEvent,
            userId,
            handleSetError,
            gdprType,
            isGDPRCountry,
            launchCommSettingsFollowAction,
        } = this.props;
        let shouldShowModal = await shouldShowGDPRModal(
            gdprType,
            isGDPRCountry,
            userId,
            launchCommSettingsFollowAction,
        );
        if (shouldShowModal) {
            trackGAEvent({
                action: GA_SHOW_GDPR_MODAL,
            });

            showOverlay('modal', {
                type: 'simple',
                noMinHeight: true,
                onClose: hideOverlay,
                children: (
                    <GDPRModalContent
                        gdprType={gdprType}
                        trackGAEvent={trackGAEvent}
                        userId={userId}
                        onCloseGDPRModal={hideOverlay}
                        handleSetError={handleSetError}
                        launchCommSettingsFollowAction={
                            launchCommSettingsFollowAction
                        }
                    />
                ),
            });
        }
    };

    _handleFollowClick = () => {
        const { onFollow, id } = this.props;

        onFollow(id).then(this._informUser);
    };

    render() {
        const {
            dataPointOne,
            id,
            imageUrl,
            isFollowing,
            isSquareImage,
            title,
            onClose,
            onLinkClick,
            subtitle,
            type,
            url,
        } = this.props;

        const organizerCardClasses = classnames(
            'follow-card',
            {
                'follow-card--grid eds-l-pad-hor-3 eds-l-pad-top-6 eds-l-pad-bot-5':
                    type === 'grid',
                'eds-l-pad-all-6': type !== 'grid',
            },
            'eds-bg-color--background',
        );

        return (
            <section key={id} className={organizerCardClasses}>
                <div className="follow-card__image_and_text">
                    <a
                        onClick={onLinkClick}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="follow-card__link"
                        title={gettext(`View ${title} profile.`)}
                        tabIndex="-1"
                        data-spec="organizer-image"
                    >
                        <OrganizerProfileImage
                            imageUrl={imageUrl}
                            organizerName={title}
                            isSquare={isSquareImage}
                        />
                    </a>

                    <a
                        onClick={onLinkClick}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="follow-card__link"
                        title={gettext(`View ${title} profile.`)}
                        data-spec="organizer-name"
                    >
                        <div className="follow-card__title-container eds-l-mar-left-6 eds-l-mar-vert-1">
                            <h4 className="follow-card__title eds-text-bl eds-text-color--ui-800">
                                {title}
                            </h4>
                            {subtitle}
                        </div>
                        {dataPointOne}
                    </a>
                </div>
                <Button
                    __containerClassName="follow-card__cta"
                    style="follow"
                    isFollowing={isFollowing}
                    onClick={this._handleFollowClick}
                >
                    {FOLLOW_CTA}
                </Button>

                <div className="follow-card__underlay">
                    <div className="follow-card__close-btn">
                        <IconButton
                            iconType={<CrossChunkySvg />}
                            iconColor="grey-600"
                            title={gettext('Remove this organizer card')}
                            onClick={onClose}
                        />
                    </div>
                </div>
            </section>
        );
    }
}

export default withOverlayControls(FollowCard);
